import React, { useState } from "react";

import { PencilAltIcon, PlusIcon, TrashIcon } from "@heroicons/react/outline";

import LoadingWheel from "../Shared/LoadingWheel";
import ConfirmDelete from "../Shared/ConfirmDelete";
import { SingleLineSettingRow } from "../Shared/SettingsRows";
import { ICTABanner } from './../../models/ctaBanner/CTABanner';
import { useCTABanners } from "../../data/ctaBanners/useCTABanners";
import { CreateUpdateCTABannerFlyout } from "./CreateUpdateCTABannerFlyout";
import { useCTABannerMutations } from "../../data/ctaBanners/useCTABannerMutations";

interface CTABannersProps {
  agencyId: string;
}

export default function CTABanners({ agencyId }: CTABannersProps) {
  const { ctaBanners } = useCTABanners(agencyId);
  const { remove } = useCTABannerMutations(agencyId);
  const [createUpdateOpen, setCreateUpdateOpen] = useState(false);
  const [updateCTABanner, setUpdateCTABanner] = useState<ICTABanner>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCTABanner, setDeleteCTABanner] = useState<ICTABanner>();

  const handleConfirmDeleteCTABanner = () => {
    if (!deleteCTABanner) return;

    remove.mutate(deleteCTABanner.id);

    setDeleteOpen(false);
    setDeleteCTABanner(undefined);
  }

  return (
    <>
      {/* @ts-ignore */}
      <ConfirmDelete
        itemName="CTA Banner"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleConfirmDeleteCTABanner}
      />
      <CreateUpdateCTABannerFlyout
        open={createUpdateOpen}
        setOpen={(v) => {
          setCreateUpdateOpen(v);
          updateCTABanner && setTimeout(() => setUpdateCTABanner(undefined), 600);
        }}
        agencyId={agencyId}
        ctaBanner={updateCTABanner}
      />
      <div className="divide-y space-y-6">
        <div className="-mx-6 px-6">
          <h3 className="text-xl font-medium text-gray-900">
            Newsletter Call-To-Action Banners
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure your Call-To-Action Banners for your newsletters here.
          </p>
          <div>
            <button
              type="button"
              onClick={() => { setCreateUpdateOpen(true); }}
              className="my-6 flex items-center justify-center w-full md:w-auto px-3 py-2 mr-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-etpink-600 hover:bg-etpink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
            >
              Add New Banner
            </button>
          </div>
        </div>
        <div>
          {ctaBanners.isLoading ? (
            <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
              <li key="settings-loading" className="py-4 flex justify-center">
                <LoadingWheel width="w-8" height="h-8" />
              </li>
            </ul>
          ) : (
            <ul className="border border-gray-200 bg-white rounded-md divide-y divide-gray-200 my-4">
              {ctaBanners.data?.data.length ?? 0 > 0 ? (
                ctaBanners.data?.data.map((banner) =>
                  <SingleLineSettingRow
                    key={banner.id}
                    label={banner.name}
                    value={
                      <div className="flex items-center">
                        <div className="ml-4 flex-shrink-0 flex items-center -mr-2">
                          <button
                            onClick={() => {
                              setDeleteCTABanner(banner);
                              setDeleteOpen(true);
                            }}
                            className="mx-2 text-gray-400 hover:text-red-600"
                          >
                            <span className="sr-only">Delete</span>
                            <TrashIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex items-center -mr-2">
                          <button
                            onClick={() => {
                              setUpdateCTABanner(banner);
                              setCreateUpdateOpen(true);
                            }}
                            className="mx-2 text-gray-400 hover:text-gray-600"
                          >
                            <span className="sr-only">Edit</span>
                            <PencilAltIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    }
                  />
                )
              ) : (
                <li>
                  <div className="py-5 text-center text-sm text-gray-400">
                    No Call-To-Action Banners
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
